import React, { useState } from 'react';
import {
  TextField,
  InputAdornment,
  IconButton,
  styled,
  Box,
} from '@mui/material';
import { Mic, AutoAwesome, Pause } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { FormGroup } from 'reactstrap';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { AIButton } from 'components/AI/AIButton/AIButton';
import { AILoadingState } from './AISharedComponents';
import { useVoiceInput } from './hooks/useVoiceInput';

interface InlineAiReplyProps {
  generateAiReply?: (request: {
    thread_id: string;
    message: string;
  }) => Promise<{ content: string; draftUuid: string | null }>;
  isAiEnabled: boolean;
  threadId: string;
  onContentGenerated: (content: string, draftUuid?: string | null) => void;
  disabled?: boolean;
}

interface FormData {
  message: string;
}

// Form validation schema - message is optional for reply mode
const replySchema = yup.object().shape({
  message: yup.string().trim().optional(),
});

// Styled components for the AI reply section
const AIReplyContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: theme.spacing(2.5),
}));

const InputContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'end',
  marginBottom: theme.spacing(1.25),
  gap: theme.spacing(0.9375),
}));

const InputWrapper = styled('div')({
  flexGrow: 1,
});

const ButtonContainer = styled('div')({
  display: 'flex',
  alignSelf: 'flex-start',
});

const VoiceIconButton = styled(IconButton, {
  shouldForwardProp: (prop) =>
    prop !== 'isListening' && prop !== 'isProcessingVoice',
})<{ isListening?: boolean; isProcessingVoice?: boolean }>(
  ({ theme, isListening, isProcessingVoice }) => ({
    color:
      isListening || isProcessingVoice
        ? theme.palette.primary.main
        : theme.palette.grey[500],
    backgroundColor: 'transparent',
    borderRadius: '50%',
    transition: 'all 0.3s ease',
    transform: isListening || isProcessingVoice ? 'scale(1.1)' : 'scale(1)',
    opacity: isProcessingVoice ? 0.7 : 1,
  }),
);

const StyledAIButton = styled(AIButton)(({ theme }) => ({
  minHeight: theme.spacing(3.125),
}));

// Styled components for animations
const PulsingMic = styled(Mic)`
  @keyframes pulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.2);
    }
    100% {
      transform: scale(1);
    }
  }

  &.listening {
    animation: pulse 0.8s infinite;
  }
`;

const AnimatedPause = styled(Pause)`
  @keyframes recording {
    0% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.6;
      transform: scale(1.1);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }

  animation: recording 1.5s ease-in-out infinite;
`;

const AnimatedIcon = styled(AutoAwesome)(({ theme }) => ({
  fontSize: theme.spacing(1.40625),
  animation: 'pulse 0.8s infinite',
}));

const VoiceIconWrapper = styled('div')(({ theme }) => ({
  fontSize: theme.spacing(1.40625),
}));

const InlineAiReply: React.FC<InlineAiReplyProps> = ({
  generateAiReply,
  isAiEnabled,
  threadId,
  onContentGenerated,
  disabled = false,
}) => {
  const [isGenerating, setIsGenerating] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    clearErrors,
    setValue,
    watch,
  } = useForm<FormData>({
    resolver: yupResolver(replySchema),
    defaultValues: { message: '' },
    mode: 'onChange',
  });

  const currentMessage = watch('message');

  // Voice input hook
  const {
    isListening,
    isProcessingVoice,
    handleVoiceInput,
    stopRecordingAndCommit,
  } = useVoiceInput({
    currentValue: currentMessage,
    setValue,
    setError,
    fieldName: 'message',
  });

  const onSubmit = async (data: FormData): Promise<void> => {
    if (!threadId) {
      setError('message', {
        type: 'manual',
        message: 'Thread ID is required to generate a reply',
      });
      return;
    }

    if (!generateAiReply) {
      setError('message', {
        type: 'manual',
        message: 'AI reply generation is not available',
      });
      return;
    }

    setIsGenerating(true);
    clearErrors();

    try {
      const request = {
        thread_id: threadId,
        message: data.message ? data.message.trim() : '',
      };
      const { content, draftUuid } = await generateAiReply(request);

      // Immediately use the generated content
      onContentGenerated(content, draftUuid ?? null);

      // Clear the prompt field after successful generation
      reset({ message: '' });
    } catch (err) {
      // Extract the actual error message from the backend
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to generate reply. Please try again.';

      setError('message', {
        type: 'manual',
        message: errorMessage,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle generate button click - stop recording if in progress, then submit
  const handleGenerateClick = (): void => {
    // Stop recording (if active) and commit transcript, then submit
    stopRecordingAndCommit(() => {
      handleSubmit(onSubmit)();
    });
  };

  // Don't render anything if AI is not enabled
  if (!isAiEnabled) {
    return null;
  }

  return (
    <FormGroup className="ai-inline-reply mb-3">
      <AIReplyContainer>
        <InputContainer>
          <InputWrapper>
            <TextField
              id="ai-prompt"
              size="small"
              fullWidth
              variant="outlined"
              {...register('message')}
              placeholder="Tell AI what you want to say and we'll draft the message for you..."
              disabled={isGenerating || disabled}
              error={Boolean(errors.message)}
              helperText={errors.message?.message}
              multiline
              minRows={1}
              maxRows={3}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <VoiceIconButton
                      edge="end"
                      size="medium"
                      disabled={isGenerating || disabled || isProcessingVoice}
                      onClick={handleVoiceInput}
                      title={(() => {
                        if (isProcessingVoice)
                          return 'Processing voice input...';
                        if (isListening) return 'Click to stop recording';
                        return 'Start voice recording';
                      })()}
                      isListening={isListening}
                      isProcessingVoice={isProcessingVoice}
                    >
                      {isProcessingVoice && (
                        <VoiceIconWrapper>
                          <AnimatedIcon />
                        </VoiceIconWrapper>
                      )}
                      {!isProcessingVoice && isListening && (
                        <VoiceIconWrapper>
                          <AnimatedPause />
                        </VoiceIconWrapper>
                      )}
                      {!isProcessingVoice && !isListening && (
                        <VoiceIconWrapper>
                          <PulsingMic />
                        </VoiceIconWrapper>
                      )}
                    </VoiceIconButton>
                  </InputAdornment>
                ),
              }}
            />
          </InputWrapper>
          <ButtonContainer>
            <StyledAIButton
              text={isGenerating ? 'Generating...' : 'Generate'}
              variant="outlined"
              onClick={handleGenerateClick}
              disabled={isGenerating || disabled}
              size="small"
            />
          </ButtonContainer>
        </InputContainer>

        {/* Loading State */}
        {isGenerating && (
          <div className="ai-loading-inline">
            <AILoadingState message="Analyzing conversation and generating reply..." />
          </div>
        )}
      </AIReplyContainer>
    </FormGroup>
  );
};

export default InlineAiReply;
